import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import apiClient from "@services/api/apiClient";
import API, { withStatus, STATUS } from "@config/shared/api-paths";
import toast from "react-hot-toast";
import i18n from "@locales/i18n";
import { ROLE_NAMES } from "@config/shared/roles";
import { initializePaths } from "@utils/shared/route-generator";
import {
  migrateUserRole,
  isTokenExpired,
  tryRefreshToken,
} from "./auth-helpers";
import {
  clearSignedOut,
  fetchActiveDirectoryToken,
  markSignedOut,
  requestActiveDirectoryAccess,
} from "@services/auth/active-directory";
import { getNoAccessReason } from "@services/auth/no-access";
import {
  initSessionSync,
  cleanupSessionSync,
  notifySessionChange,
  notifyLogout,
  onSessionChange,
} from "@utils/shared/session-storage-sync";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [features, setFeatures] = useState([]); // Always initialize as empty array, never undefined
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [routesReady, setRoutesReady] = useState(false); // New flag to track if routes are mounted

  // Last-APPLIED user/features as their JSON strings. The cross-tab SESSION_DATA
  // handler dedups against THESE, not against sessionStorage — the sync layer
  // overwrites sessionStorage with the incoming payload BEFORE the handler runs,
  // so a sessionStorage comparison always looked "unchanged" and setUser/
  // setFeatures never fired cross-tab. Refs stay current without re-subscribing.
  const appliedUserRef = useRef(
    typeof sessionStorage !== "undefined" ? sessionStorage.getItem("user") : null,
  );
  const appliedFeaturesRef = useRef(
    typeof sessionStorage !== "undefined" ? sessionStorage.getItem("features") : null,
  );

  // Keep the dedup refs in lock-step with state from EVERY source (initAuth,
  // login, migration, cross-tab), so the SESSION_DATA handler's change-detection
  // is always comparing against what is actually applied.
  useEffect(() => {
    appliedUserRef.current = user ? JSON.stringify(user) : null;
  }, [user]);
  useEffect(() => {
    appliedFeaturesRef.current = JSON.stringify(features);
  }, [features]);

  // `notifyKeys` scopes the cross-tab broadcast. Default = features-only (a
  // frequent, token-free event: mount refresh, feature toggle). Pass null for a
  // full broadcast (login) so a logged-out sibling tab boots into the session.
  const loadFeatures = useCallback(async (notifyKeys = ["features"]) => {
    setFeaturesLoading(true);
    try {
      const { data: featuresData } = await apiClient.get(
        withStatus(API.BACKEND.FEATURES, STATUS.PUBLISHED),
      );

      // Backend returns {roots: [...]} - extract the roots array
      let features = [];
      if (Array.isArray(featuresData?.roots)) {
        features = featuresData.roots;
      } else if (Array.isArray(featuresData)) {
        // Fallback: if backend returns array directly
        features = featuresData;
      }

      // Initialize paths from features tree
      initializePaths(features);

      // Store in sessionStorage - features are user/session specific
      const featuresJson = JSON.stringify(features);
      sessionStorage.setItem("features", featuresJson);
      appliedFeaturesRef.current = featuresJson; // keep dedup ref in lock-step
      setFeatures(features);

      // Notify other tabs (features-only by default — never carries tokens).
      notifySessionChange(notifyKeys);

      return features;
    } catch (err) {
      console.error("Failed to fetch features:", err);
      setFeatures([]); // Always set to empty array, never undefined
      return [];
    } finally {
      setFeaturesLoading(false);
    }
  }, []);

  // Fetch features from API
  const fetchFeatures = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    return loadFeatures();
  }, [isAuthenticated, loadFeatures]);

  // Apply a KNOWN features tree IMMEDIATELY (optimistic) — no backend round-trip.
  // A feature toggle calls this so routes/paths update and sibling tabs are
  // notified at the same instant the menu updates, instead of waiting on the
  // eventually-consistent backend. A background reconcile (fetchFeatures) then
  // corrects any drift (e.g. re-parenting). Broadcast is features-only (no tokens).
  const applyFeatures = useCallback((featuresTree) => {
    const arr = Array.isArray(featuresTree) ? featuresTree : [];
    const json = JSON.stringify(arr);
    initializePaths(arr);
    sessionStorage.setItem("features", json);
    appliedFeaturesRef.current = json;
    setFeatures(arr);
    notifySessionChange(["features"]);
    return arr;
  }, []);

  // Logout function (defined early so validateAndRefreshToken can use it)
  const logout = useCallback(async (options = {}) => {
    const { showToast = true } = options;

    try {
      await apiClient.post(API.AUTH.SIGN_OUT);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // An explicit sign-out: remembered here and in the other tabs, so an
      // ActiveDirectory tenant's login page doesn't sign straight back in.
      notifyLogout({ signedOut: true });
      sessionStorage.clear();
      sessionStorage.removeItem("user");
      markSignedOut();

      if (showToast) {
        toast.success(i18n.t("userLogin.loggedOut"));
      }

      // Hard redirect clears all React and Router state (including location.state.from)
      // This ensures login page has no "from" → user goes to /home after re-login
      window.location.replace("/auth/login");
    }
  }, []);

  // Soft logout for session expiry — clears auth state but stays in React
  // so ProtectedRoute re-renders and passes state.from to the login page
  const softLogout = useCallback(() => {
    notifyLogout();
    sessionStorage.clear();
    sessionStorage.removeItem("user");
    setUser(null);
    setFeatures([]);
    setIsAuthenticated(false);
  }, []);

  // Token validation and refresh mechanism
  const validateAndRefreshToken = useCallback(async () => {
    const accessToken = sessionStorage.getItem("accessToken");
    const refreshToken = sessionStorage.getItem("refreshToken");

    if (!accessToken || !refreshToken) {
      if (isAuthenticated) softLogout();
      return false;
    }

    try {
      if (!isTokenExpired(accessToken)) return true;
    } catch (error) {
      console.error("[Auth] Token validation failed:", error);
      softLogout();
      return false;
    }

    console.debug("[Auth] Token expired, attempting refresh...");
    const refreshed = await tryRefreshToken(refreshToken);
    // 403: the session is already ended and the browser is redirecting to the
    // No Access page — don't softLogout, which would route to /auth/login instead.
    if (refreshed?.noAccess) return false;
    if (refreshed) return true;

    console.error("[Auth] Token refresh failed, logging out");
    softLogout();
    return false;
  }, [isAuthenticated, softLogout]);

  // Initialize auth state from storage
  useEffect(() => {
    // Initialize session sync for new tabs
    initSessionSync();

    const initAuth = () => {
      const storedUser = sessionStorage.getItem("user");
      const storedFeatures = sessionStorage.getItem("features");
      const accessToken = sessionStorage.getItem("accessToken");

      if (storedUser && accessToken) {
        const parsedUser = JSON.parse(storedUser);

        // Migrate user format if needed
        migrateUserRole(parsedUser);
        sessionStorage.setItem("user", JSON.stringify(parsedUser));

        setUser(parsedUser);
        setIsAuthenticated(true);

        // Load cached features from sessionStorage
        if (storedFeatures) {
          try {
            const parsedFeatures = JSON.parse(storedFeatures);
            // Ensure it's an array
            const features = Array.isArray(parsedFeatures)
              ? parsedFeatures
              : [];

            // Initialize paths from cached features
            if (features.length > 0) {
              initializePaths(features);
            }

            setFeatures(features);
          } catch (error) {
            console.error("Failed to parse cached features:", error);
            setFeatures([]);
          }
        } else {
          setFeatures([]);
        }
      }
      setLoading(false);

      // Give React Router time to mount routes after features are loaded
      setTimeout(() => {
        setRoutesReady(true);
      }, 100);
    };

    initAuth();

    // Subscribe to session changes from other tabs
    const unsubscribe = onSessionChange((data) => {
      if (data.type === "LOGOUT") {
        // Another tab logged out
        console.debug("[Auth] Logout detected from another tab");
        sessionStorage.clear();
        sessionStorage.removeItem("user");
        if (data.signedOut) markSignedOut();
        setUser(null);
        setFeatures([]);
        setIsAuthenticated(false);
        setLoading(false); // Ensure we're not stuck in loading state
        toast.info(i18n.t("userLogin.loggedOutOtherTab"));
      } else if (data.type === "SESSION_DATA") {
        // Another tab updated session (e.g., refreshed token, updated features)
        console.debug("[Auth] Session update detected from another tab");

        // Only update state when values ACTUALLY changed — unnecessary updates
        // cause a full React tree re-render from AuthProvider root, which
        // remounts the TipTap editor and destroys all React node/mark views.
        // Compare against the last-APPLIED ref, NOT sessionStorage: the sync
        // layer already overwrote sessionStorage with this payload, so a
        // sessionStorage comparison always reads "unchanged" and these updates
        // never fired cross-tab (a feature enabled in another tab stayed
        // invisible/unroutable here until a manual reload).
        if (data.user && data.user !== appliedUserRef.current) {
          try {
            const parsedUser = JSON.parse(data.user);
            appliedUserRef.current = data.user;
            // Write sessionStorage too so the handler is self-consistent even if
            // the sync layer did not pre-write it (dedup is ref-based, not
            // storage-based, so this can't re-break cross-tab detection).
            sessionStorage.setItem("user", data.user);
            setUser(parsedUser);
          } catch (error) {
            console.error("Failed to parse user from sync:", error);
          }
        }

        if (data.features && data.features !== appliedFeaturesRef.current) {
          try {
            const parsedFeatures = JSON.parse(data.features);
            appliedFeaturesRef.current = data.features;
            sessionStorage.setItem("features", data.features);
            setFeatures(Array.isArray(parsedFeatures) ? parsedFeatures : []);
          } catch (error) {
            console.error("Failed to parse features from sync:", error);
          }
        }

        // A SESSION_DATA message only ever CONFIRMS a session (login/refresh in
        // another tab). Logout is signalled separately via the LOGOUT message,
        // so never downgrade to logged-out here — an empty/partial payload must
        // not flip an authenticated tab back to the login screen.
        if (data.accessToken) {
          setIsAuthenticated((prev) => (prev ? prev : true));
        }
      }
    });

    // Cleanup on unmount
    return () => {
      cleanupSessionSync();
      unsubscribe();
    };
  }, []);

  // Periodic token validation and refresh (every 10 seconds)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Check token immediately when authenticated
    validateAndRefreshToken();

    // Set up interval to check every 10 seconds
    const intervalId = setInterval(() => {
      validateAndRefreshToken();
    }, 10000); // 10 seconds

    // Check token when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        console.debug("[Auth] Tab became visible, checking token...");
        validateAndRefreshToken();
      }
    };

    // Check token when window gains focus
    const handleFocus = () => {
      if (isAuthenticated) {
        console.debug("[Auth] Window gained focus, checking token...");
        validateAndRefreshToken();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isAuthenticated, validateAndRefreshToken]);

  // Shared by every sign-in path (Local, AD silent, AD form): `requestTokens`
  // resolves with the backend's {access, refresh} pair; this stores it and
  // boots the session exactly the same way regardless of how it was obtained.
  const signInWith = useCallback(
    async (requestTokens) => {
      try {
        const { access, refresh } = await requestTokens();

        // Decode access token to get user info (JWT payload)
        const base64 = access
          .split(".")[1]
          .replace(/-/g, "+")
          .replace(/_/g, "/");
        const decoded = JSON.parse(
          decodeURIComponent(
            atob(base64)
              .split("")
              .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
              .join(""),
          ),
        );

        const user = {
          id: decoded.id,
          name: decoded.name,
          role: ROLE_NAMES[decoded.role] || "member", // Map numeric role to string
          roleId: decoded.role, // Keep original numeric role
          groups: decoded.groups,
          tenant: decoded.tenant,
          locale: "nl-NL", // Default locale
        };

        // Store tokens in sessionStorage (cleared on browser close)
        sessionStorage.setItem("accessToken", access);
        sessionStorage.setItem("refreshToken", refresh);

        // Store user data in sessionStorage (cleared on browser close,
        // matching the auth tokens — no persistent storage of auth data)
        sessionStorage.setItem("user", JSON.stringify(user));
        // Signed in again → an earlier sign-out no longer holds back the AD
        // silent sign-in. Before the broadcast below, which shares all keys.
        clearSignedOut();

        setUser(user);
        // Full broadcast (null = all keys, incl. fresh tokens) so a logged-out
        // sibling tab boots into this new session. The token pair is brand-new
        // here, so it can't downgrade anyone.
        const features = await loadFeatures(null);
        setIsAuthenticated(true);

        toast.success(i18n.t("userLogin.loginSuccess"));

        return { success: true, user, features };
      } catch (error) {
        const isNetworkError =
          error instanceof TypeError && error.message === "Failed to fetch";
        // 403 = a known user without access → callers show the No Access page.
        // `status` lets callers tell e.g. a 401 from a server error.
        return {
          success: false,
          isNetworkError,
          status: error?.status ?? null,
          noAccessReason: getNoAccessReason(error),
          error: error.message,
        };
      }
    },
    [loadFeatures],
  );

  // Local login — /api/v1/auth/access with username and password
  const login = useCallback(
    (credentials) =>
      signInWith(async () => {
        const { data } = await apiClient.post(API.AUTH.ACCESS, {
          username: credentials.username || credentials.email,
          password: credentials.password,
        });
        return data;
      }),
    [signInWith],
  );

  // ActiveDirectory silent sign-in (Windows Integrated Auth, no form)
  const loginWithActiveDirectory = useCallback(
    () => signInWith(fetchActiveDirectoryToken),
    [signInWith],
  );

  // ActiveDirectory fallback — AD username (UPN) and password
  const loginWithActiveDirectoryCredentials = useCallback(
    (credentials) =>
      signInWith(() => requestActiveDirectoryAccess(credentials)),
    [signInWith],
  );

  // Change password function
  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      await apiClient.post(API.AUTH.CHANGE_PASSWORD, {
        oldPassword,
        newPassword,
      });
      toast.success(i18n.t("profile.passwordChanged"));
      return { success: true };
    } catch (error) {
      const message = error.message || "Password change failed";
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  // Forgot password function
  const forgotPassword = useCallback(async (username) => {
    try {
      await apiClient.post(API.AUTH.FORGOT_PASSWORD, { username });
      toast.success(i18n.t("userLogin.passwordResetLinkSent"));
      return { success: true };
    } catch (error) {
      const message = error.message || "Request failed";
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  // Reset password function
  const resetPassword = useCallback(async (token, password) => {
    try {
      await apiClient.post(API.AUTH.RESET_PASSWORD, { token, password });
      toast.success(i18n.t("userLogin.passwordResetSuccessLogin"));
      return { success: true };
    } catch (error) {
      const message = error.message || "Password reset failed";
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  // Update user profile
  const updateProfile = useCallback(
    async (updates) => {
      try {
        await apiClient.put("/admin/users/" + user.id, updates);
        const updatedUser = { ...user, ...updates };

        sessionStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);

        toast.success(i18n.t("userLogin.profileUpdatedSuccess"));
        return { success: true, user: updatedUser };
      } catch (error) {
        const message = error.message || "Update failed";
        toast.error(message);
        return { success: false, error: message };
      }
    },
    [user],
  );

  // Check if user has access to a feature
  const hasFeatureAccess = useCallback(
    (featureHandle) => {
      if (!features || features.length === 0) return false;

      const checkFeature = (featureList) => {
        for (const featureWrapper of featureList) {
          const feature = featureWrapper.node || featureWrapper;
          const children = featureWrapper.children || [];

          if (feature.handle === featureHandle) return true;
          if (children.length > 0 && checkFeature(children)) return true;
        }
        return false;
      };

      return checkFeature(features);
    },
    [features],
  );

  // Get feature by handle
  const getFeature = useCallback(
    (featureHandle) => {
      if (!features || features.length === 0) return null;

      const findFeature = (featureList) => {
        for (const featureWrapper of featureList) {
          const feature = featureWrapper.node || featureWrapper;
          const children = featureWrapper.children || [];

          if (feature.handle === featureHandle) return feature;
          if (children.length > 0) {
            const found = findFeature(children);
            if (found) return found;
          }
        }
        return null;
      };

      return findFeature(features);
    },
    [features],
  );

  const value = useMemo(
    () => ({
      user,
      features,
      loading: loading || !routesReady, // Keep loading until routes are ready
      featuresLoading,
      isAuthenticated,
      login,
      loginWithActiveDirectory,
      loginWithActiveDirectoryCredentials,
      logout,
      changePassword,
      forgotPassword,
      resetPassword,
      updateProfile,
      fetchFeatures,
      applyFeatures,
      hasFeatureAccess,
      getFeature,
    }),
    [
      user,
      features,
      loading,
      routesReady,
      featuresLoading,
      isAuthenticated,
      login,
      loginWithActiveDirectory,
      loginWithActiveDirectoryCredentials,
      logout,
      changePassword,
      forgotPassword,
      resetPassword,
      updateProfile,
      fetchFeatures,
      applyFeatures,
      hasFeatureAccess,
      getFeature,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
